import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import BlockIcon from '@mui/icons-material/Block';
import PaymentService from '../../Services/payment.service.js';
import CustomPaymentDialog from './CustomPaymentDialog';
import Swal from 'sweetalert2';
import { invalidateMainPageCache } from '../../Utils/cacheManager';

/** Parse API month value (e.g. "2026-03-01T00:00:00") as UTC so March doesn't become Feb 28 in local timezone. */
function parseMonthAsUtc(value) {
  if (value == null) return null;
  if (value instanceof Date) {
    return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), 1));
  }
  const s = String(value).trim();
  if (!s) return null;
  const hasTimezone = /[Z+-]/.test(s);
  const normalized = hasTimezone ? s : s + 'Z';
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
}

const ClientBlockerWindow = ({ open, onClose, client, token, onSuccess, showMoveButton = true, accountantName, accountantId, userType }) => {
  const { t,i18n } = useTranslation();
  const direction = i18n.dir();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [balanceData, setBalanceData] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [accountantHasCard, setAccountantHasCard] = useState(false);
  const [previewAmount, setPreviewAmount] = useState(0);

  const checkAccountantCard = async () => {
    if (!accountantId) return;
    try {
      const paymentMethod = await PaymentService.accountant.getPaymentMethod(accountantId);
      setAccountantHasCard(paymentMethod != null && paymentMethod.last4 != null);
    } catch (err) {
      console.error('Error checking accountant card:', err);
      setAccountantHasCard(false);
    }
  };

  const displayUnpaid = (() => {
    if (!balanceData) return 0;
    const netBalance = Number(balanceData.totals?.netBalance ?? 0);
    return Math.max(0, netBalance);
  })();
  const hasUnpaidMonths = (balanceData?.months ?? []).some(m => (m.netBalance ?? 0) > 0);
  const showAmountSection = balanceData && (displayUnpaid > 0 || hasUnpaidMonths);

  const now = new Date();
  const currentMonthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const isPaymentForFutureMonth = (balanceData?.months ?? []).some(m => {
    if ((m.netBalance ?? 0) <= 0) return false;
    const [y, mo] = (m.monthKey || '').split('-').map(Number);
    if (!y || !mo) return false;
    return new Date(Date.UTC(y, mo - 1, 1)) > currentMonthStart;
  });

  useEffect(() => {
    if (!open || !client?.caseId || !token) {
      setBalanceData(null);
      setAccountantHasCard(false);
      setPreviewAmount(0);
      setError('');
      return;
    }
    let cancelled = false;
    setError('');
    setLoadingBalance(true);

    PaymentService.accountant.previewUnpaidMonths(client.caseId)
      .then((result) => {
        if (cancelled) return;
        if (result && result.data) {
          setBalanceData(result.data);
          const netBalance = Number(result.amount ?? result.data?.totals?.netBalance ?? 0);
          setPreviewAmount(netBalance > 0 ? netBalance : 0);
        } else {
          setBalanceData(null);
          setPreviewAmount(0);
          setError(result?.error || t('errorLoadingBalance') || 'Error loading balance');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setBalanceData(null);
          setPreviewAmount(0);
          setError(err.message || t('errorLoadingBalance') || 'Error loading balance');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingBalance(false);
      });
    if (showMoveButton && accountantId) {
      checkAccountantCard();
    }
    return () => { cancelled = true; };
  }, [open, client?.caseId, token, accountantId, showMoveButton]);

  const handleMoveToAccountant = async () => {
    if (!client?.caseId || !accountantId || !balanceData) return;

    const amountToShow = displayUnpaid > 0 ? displayUnpaid : 0;
    const unpaidCount = (balanceData.months || []).filter(m => (m.netBalance ?? 0) > 0).length;
    const needsPayment = amountToShow > 0;

    const confirmHtml = needsPayment
      ? `
        <p>${t('moveToAccountantPayMessage') || 'רואה החשבון ישלם את יתרת החוב של הלקוח ויקבל את אחריות התשלום.'}</p>
        <p>${t('paymentAmount') || 'סכום התשלום'}: <strong>₪${amountToShow.toFixed(2)}</strong></p>
        <p>${t('paymentForClient') || 'תשלום עבור הלקוח'}: <strong>${client.name || client.caseId}</strong></p>
        <p>${t('unpaidMonthsCount') || 'מספר חודשים שלא שולמו'}: <strong>${unpaidCount}</strong></p>
        <p>${t('paymentConfirmationMessage') || 'האם אתה בטוח שברצונך לשלם ולהעביר לרואה חשבון?'}</p>
      `
      : `
        <p>${t('moveToAccountantNoChargeMessage') || 'אין יתרה לתשלום. החיוב של החודשים הבאים יועבר לרואה החשבון.'}</p>
        <p><strong>${client.name || client.caseId}</strong></p>
        <p>${t('confirmMoveToAccountant') || 'לאשר העברה?'}</p>
      `;

    const confirmResult = await Swal.fire({
      title: t('moveToAccountant') || 'העבר לרואה חשבון',
      html: confirmHtml,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: t('confirm') || 'אישור',
      cancelButtonText: t('cancel') || 'ביטול',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    });

    if (!confirmResult.isConfirmed) return;

    if (!needsPayment) {
      await executeMoveToAccountant({ UseSavedCard: true, Payment: null });
    } else if (accountantHasCard) {
      await executeMoveToAccountant({ UseSavedCard: true, Payment: null });
    } else {
      setShowPaymentDialog(true);
    }
  };

  const executeMoveToAccountant = async ({ UseSavedCard, Payment }) => {
    if (!client?.caseId || !accountantId) return;

    const amountToSend = displayUnpaid > 0 ? displayUnpaid : previewAmount;

    setLoading(true);
    setError('');
    try {
      const result = await PaymentService.accountant.moveClientToAccountant({
        CaseId: client.caseId,
        AccountantId: accountantId,
        ExpectedAmount: amountToSend,
        UseSavedCard,
        Payment,
      });
      if (!result?.success) throw new Error(result?.error || 'Request failed');

      await Swal.fire({
        title: t('moveToAccountantSuccess') || 'הועבר לרואה חשבון',
        text: t('moveToAccountantSuccessMessage') || 'התשלום בוצע בהצלחה. הלקוח שוחרר והחיוב הועבר לרואה החשבון.',
        icon: 'success',
        confirmButtonText: t('ok') || 'אישור'
      });

      if (accountantId) invalidateMainPageCache(accountantId);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || (err?.error) || t('moveToAccountantFailed') || 'העברה נכשלה');
      await Swal.fire({
        title: t('error') || 'שגיאה',
        text: err.message || err?.error || t('moveToAccountantFailed') || 'העברה נכשלה',
        icon: 'error',
        confirmButtonText: t('ok') || 'אישור'
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      dir={direction}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <BlockIcon color="error" />
          <Typography variant="h6">
            {t('clientBlocked') || 'לקוח חסום'}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            {t('clientBlockedMessage') || 'הלקוח חסום ואינו יכול להיכנס למערכת'}
          </Typography>
          {client?.name && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>{t('clientName') || 'שם לקוח'}:</strong> {client.name}
            </Typography>
          )}
          {client?.caseId && (
            <Typography variant="body2">
              <strong>{t('caseId') || 'מזהה תיק'}:</strong> {client.caseId}
            </Typography>
          )}
        </Alert>

        {showMoveButton && (
          <>
            <Typography variant="body2" sx={{ mt: 2, mb: 2 }}>
              {t('clientBlockedSolution', { 
                clientName: client?.name || t('clientName') || 'הלקוח',
                accountantName: accountantName || t('accountant') || 'רואה החשבון'
              }) || `כדי לאפשר ללקוח ${client?.name || 'הלקוח'} להיכנס למערכת, יש לשלם את החודשים שלא שולמו`}
            </Typography>

            {loadingBalance ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography>{t('loadingBalance') || 'טוען יתרות...'}</Typography>
              </Box>
            ) : balanceData && !showAmountSection ? (
              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                {t('noUnpaidBalance') || 'אין יתרה לתשלום. ניתן להעביר את הלקוח לרואה החשבון ללא חיוב.'}
              </Alert>
            ) : showAmountSection && (
              <Box sx={{ mt: 2, mb: 2 }}>
                {isPaymentForFutureMonth && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('clientBlockedPaymentNextMonthMessage') || 'חלק מהחיובים הם לחודשים הבאים. רואה החשבון ישלם את היתרה ויקבל את אחריות התשלום.'}
                  </Alert>
                )}
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {t('balanceSummary') || 'סיכום יתרות'}:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'error.main' }}>
                  <strong>{t('unpaidBalance') || 'יתרה לתשלום'}:</strong> ₪{displayUnpaid.toFixed(2)}
                </Typography>

                {balanceData.months && balanceData.months.length > 0 && (
                  <TableContainer component={Paper} sx={{ maxHeight: 350, mb: 2 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>{t('month') || 'חודש'}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">{t('charges') || 'חיובים'}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">{t('otc') || 'חד פעמי'}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">{t('refunds') || 'החזרים'}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">{t('discounts') || 'הנחות'}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">{t('payments') || 'תשלומים'}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">{t('balance') || 'יתרה'}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(balanceData.months || []).map((m, idx) => {
                          const [y, mo] = (m.monthKey || '').split('-').map(Number);
                          const monthLabel = y && mo
                            ? new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('he-IL', { timeZone: 'UTC', year: 'numeric', month: 'short' })
                            : m.monthKey;
                          return (
                            <TableRow key={idx}>
                              <TableCell sx={{ fontSize: 11 }}>{monthLabel}</TableCell>
                              <TableCell sx={{ fontSize: 11 }} align="right">₪{(m.charges ?? 0).toFixed(2)}</TableCell>
                              <TableCell sx={{ fontSize: 11 }} align="right">₪{(m.oneTimeCharges ?? 0).toFixed(2)}</TableCell>
                              <TableCell sx={{ fontSize: 11 }} align="right">₪{(m.refunds ?? 0).toFixed(2)}</TableCell>
                              <TableCell sx={{ fontSize: 11 }} align="right">₪{(m.discounts ?? 0).toFixed(2)}</TableCell>
                              <TableCell sx={{ fontSize: 11 }} align="right">₪{(m.payments ?? 0).toFixed(2)}</TableCell>
                              <TableCell sx={{ fontSize: 11, fontWeight: 700, color: (m.netBalance ?? 0) > 0 ? 'error.main' : 'success.main' }} align="right">
                                ₪{(m.netBalance ?? 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>{t('total') || 'סה״כ'}</TableCell>
                          <TableCell sx={{ fontSize: 11, fontWeight: 700 }} align="right">₪{(balanceData.totals?.totalCharges ?? 0).toFixed(2)}</TableCell>
                          <TableCell sx={{ fontSize: 11, fontWeight: 700 }} align="right">₪{(balanceData.totals?.totalOneTimeCharges ?? 0).toFixed(2)}</TableCell>
                          <TableCell sx={{ fontSize: 11, fontWeight: 700 }} align="right">₪{(balanceData.totals?.totalRefunds ?? 0).toFixed(2)}</TableCell>
                          <TableCell sx={{ fontSize: 11, fontWeight: 700 }} align="right">₪{(balanceData.totals?.totalDiscounts ?? 0).toFixed(2)}</TableCell>
                          <TableCell sx={{ fontSize: 11, fontWeight: 700 }} align="right">₪{(balanceData.totals?.totalPayments ?? 0).toFixed(2)}</TableCell>
                          <TableCell sx={{ fontSize: 11, fontWeight: 700, color: (balanceData.totals?.netBalance ?? 0) > 0 ? 'error.main' : 'success.main' }} align="right">
                            ₪{(balanceData.totals?.netBalance ?? 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </>
        )}
        {!showMoveButton && (
          <Typography variant="body2" sx={{ mt: 2, mb: 2 }}>
            {t('clientBlockedSubAccountantMessage') || 'הלקוח חסום ואינו יכול להיכנס למערכת. יש לפנות לרואה החשבון כדי לשלם את החודשים שלא שולמו.'}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('close') || 'סגור'}
        </Button>
        {showMoveButton && balanceData && !loadingBalance && (
          <Button
            variant="contained"
            color={displayUnpaid > 0 ? "warning" : "primary"}
            onClick={handleMoveToAccountant}
            disabled={loading || loadingBalance}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{ mr: 1 }}
          >
            {loading 
              ? t('processing')
              : displayUnpaid > 0
                ? (t('moveToAccountantAndPay') || `העבר לרואה חשבון ושלם (₪${displayUnpaid.toFixed(2)})`)
                : (t('moveToAccountantNoPay') || 'העבר לרואה חשבון (ללא תשלום)')
            }
          </Button>
        )}
      </DialogActions>

      {showPaymentDialog && (
        <CustomPaymentDialog
          open={showPaymentDialog}
          onClose={() => {
            setShowPaymentDialog(false);
            setLoading(false);
          }}
          amount={displayUnpaid > 0 ? displayUnpaid : previewAmount}
          accountantId={accountantId}
          caseId={client?.caseId}
          userType={userType || 2}
          token={token}
          onSuccess={async (paymentData) => {
            setShowPaymentDialog(false);
            await executeMoveToAccountant({
              UseSavedCard: false,
              Payment: paymentData || null,
            });
          }}
          onError={async (error) => {
            await Swal.fire({
              title: t('paymentFailed') || 'התשלום נכשל',
              text: error || t('paymentError') || 'אירעה שגיאה',
              icon: 'error',
              confirmButtonText: t('ok') || 'אישור'
            });
          }}
        />
      )}
    </Dialog>
  );
};

export default ClientBlockerWindow;
